"use client";

import { useEffect, useRef, type ReactNode } from "react";

import { appSurfaceStyle, type AppThemeTone } from "@/components/editorial/product-bridge/app-surface";

/**
 * Gives the product's own popups the application's theme. Base UI portals
 * tooltips, menus and dialogs to the end of <body>, outside the AppSurface
 * whose tokens they read. While the pointer or focus is inside this scope,
 * each portal Base UI opens is marked as an app surface in the same tone, so
 * an enlarged photograph or a tooltip renders as it does in the app.
 */
export function AppPortalScope({
  tone,
  children,
}: {
  tone: AppThemeTone;
  children: ReactNode;
}) {
  const scopeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let inside = false;
    const track = (event: Event) => {
      const target = event.target;
      inside = target instanceof Node && Boolean(scopeRef.current?.contains(target));
    };
    const style = appSurfaceStyle(tone);
    const adopt = (node: Node) => {
      if (!(node instanceof HTMLElement) || !node.hasAttribute("data-base-ui-portal")) return;
      node.setAttribute("data-product-bridge", "");
      node.setAttribute("data-app-surface", tone);
      node.classList.toggle("dark", tone === "dark");
      for (const [property, value] of Object.entries(style)) {
        node.style.setProperty(
          property.startsWith("--")
            ? property
            : property.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`).replace(/^(webkit|moz)-/, "-$1-"),
          String(value),
        );
      }
    };
    const observer = new MutationObserver((records) => {
      if (!inside) return;
      for (const record of records) record.addedNodes.forEach(adopt);
    });
    observer.observe(document.body, { childList: true });
    document.addEventListener("pointerover", track, true);
    document.addEventListener("focusin", track, true);
    return () => {
      observer.disconnect();
      document.removeEventListener("pointerover", track, true);
      document.removeEventListener("focusin", track, true);
    };
  }, [tone]);

  return (
    <div ref={scopeRef} className="contents">
      {children}
    </div>
  );
}
